import React, { useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import {
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import _ from 'lodash'

import { Status } from '~/constants/data-field'
import { useGrowCropsStore } from '~/store/variety/growcrops'
import { VorietyTransterInputProps, VarietyProps } from '~/types/grow-crops-types'

import { ModalPostTechnologyGenetic } from '../modal-post'
import { useStyles } from './styles'

interface Props {
  nameField: string
  label: string
}

const CULTURA_SOJA = '1'

const GrowCropsForms: React.FC = () => {
  const [title, setTitle] = useState<Props>({} as Props)
  const classes = useStyles()
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [variety, setVariety] = useState<VarietyProps>({} as VarietyProps)

  const {
    cultureList,
    geneticList,
    tecnologyList,
    setCultureValue,
    modalPostController,
    messageError,
    cultureState,
  } = useGrowCropsStore()

  const {
    control,
    formState: { errors },
    getValues,
  } = useFormContext<VorietyTransterInputProps>()

  useEffect(() => {
    const formValues = getValues()

    if (formValues.cultura_id) {
      setVariety((prev) => ({
        ...prev,
        cultura_id: formValues.cultura_id,
      }))

      setCultureValue(formValues.cultura_id)
    }
  }, [getValues])

  const handleDisableMaturationField = (cultureId: string | undefined): boolean => {
    return Number(cultureId) === Number(CULTURA_SOJA)
  }

  const handlePopperForms = (label: string, nameField: string) => {
    modalPostController(true)
    setTitle({
      label,
      nameField,
    })
  }

  return (
    <>
      <Grid container spacing={3} className={classes.modalStyle}>
        <Grid item sm={6}>
          <Controller
            name="cultura_id"
            control={control}
            render={({ field }) => (
              <FormControl variant="outlined" className={classes.selectInput}>
                <InputLabel id="cultura_label">Cultura*</InputLabel>
                <Select
                  {...field}
                  labelId="cultura_label"
                  id="textfield-cultura"
                  label="Cultura"
                  autoWidth
                  required
                  error={(!!errors?.cultura_id || !_.isEmpty(messageError?.cultura_id)) && true}
                  value={field.value || ''}
                  onChange={(e) => {
                    const selectedCultureId = e.target.value
                    field.onChange(e)
                    setTimeout(() => {
                      field.onChange({ target: { value: selectedCultureId } })
                    }, 0)
                    setCultureValue(selectedCultureId as string)
                    setVariety((prev) => ({
                      ...prev,
                      cultura_id: String(selectedCultureId),
                    }))
                  }}
                >
                  {cultureList.map((culture) => (
                    <MenuItem key={culture.id} value={culture.id}>
                      {culture.nome}
                    </MenuItem>
                  ))}
                </Select>
                {errors.cultura_id?.message && (
                  <FormHelperText className={classes.helperText}>
                    {errors.cultura_id?.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>

        <Grid item sm={6}>
          <Controller
            name="nome"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                required
                id="textfield-nome"
                variant="outlined"
                label="Nome"
                placeholder="Nome"
                className={classes.selectInput}
                error={!!errors?.nome}
                FormHelperTextProps={{
                  className: classes.helperText,
                }}
                helperText={errors.nome?.message}
              />
            )}
          />
        </Grid>

        <Grid item sm={6}>
          <Paper className={classes.root}>
            <IconButton
              color="primary"
              className={classes.iconButton}
              onClick={() => handlePopperForms('Genética', 'genetica')}
            >
              <AddIcon />
            </IconButton>
            <Controller
              name="genetica_id"
              control={control}
              render={({ field }) => (
                <FormControl variant="outlined" className={classes.selectInput}>
                  <InputLabel id="geneticaLabel">Genética*</InputLabel>
                  <Select
                    labelId="geneticaLabel"
                    id="textfield-genetica"
                    label="Genetica"
                    autoWidth
                    required
                    error={(!!errors?.genetica_id || !_.isEmpty(messageError?.genetica_id)) && true}
                    {...field}
                  >
                    {geneticList.map((Option) => (
                      <MenuItem key={Option.id} value={Option.id}>
                        {Option.nome}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.genetica_id?.message && (
                    <FormHelperText className={classes.helperText}>
                      {errors.genetica_id?.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Paper>
        </Grid>

        <Grid item sm={6}>
          <Paper className={classes.root}>
            <IconButton
              color="primary"
              className={classes.iconButton}
              onClick={() => handlePopperForms('Tecnologia', 'tecnologia')}
            >
              <AddIcon />
            </IconButton>
            <Controller
              name="tecnologia"
              control={control}
              render={({ field }) => (
                <FormControl variant="outlined" className={classes.selectInput}>
                  <InputLabel id="tecnologiaLabel">Tecnologia*</InputLabel>
                  <Select
                    labelId="tecnologiaLabel"
                    id="textfield-tecnologia"
                    label="Tecnologia"
                    autoWidth
                    required
                    error={(!!errors?.tecnologia || !_.isEmpty(messageError?.tecnologia)) && true}
                    {...field}
                  >
                    {tecnologyList.map((Option) => (
                      <MenuItem key={Option.id} value={Option.id}>
                        {Option.nome}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.tecnologia?.message && (
                    <FormHelperText className={classes.helperText}>
                      {errors.tecnologia?.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Paper>
        </Grid>

        <Grid item sm={6}>
          <Controller
            name="grupo_maturacao"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                id="textfield-maturacao"
                variant="outlined"
                label="Grupo de Maturação"
                disabled={!handleDisableMaturationField(cultureState)}
                required={handleDisableMaturationField(cultureState)}
                placeholder="Grupo de Maturação"
                className={classes.selectInput}
                error={
                  (!!errors?.grupo_maturacao ||
                    !_.isEmpty(messageError?.variedade?.grupo_maturacao)) &&
                  true
                }
                FormHelperTextProps={{
                  className: classes.helperText,
                }}
                helperText={
                  errors.grupo_maturacao?.message ?? messageError?.variedade?.grupo_maturacao
                }
              />
            )}
          />
        </Grid>

        {Number(cultureState) === Number(CULTURA_SOJA) && (
          <Grid item sm={6}>
            <Controller
              name="sts"
              control={control}
              shouldUnregister
              render={({ field }) => (
                <FormControl variant="outlined" className={classes.selectInput}>
                  <InputLabel id="stsLabel">Tecnologia STS*</InputLabel>
                  <Select
                    labelId="stsLabel"
                    id="textfield-sts"
                    label="Tecnologia STS"
                    autoWidth
                    required
                    error={(!!errors?.sts || !_.isEmpty(messageError?.variedade?.sts)) && true}
                    {...field}
                    value={field.value ?? ''}
                  >
                    <MenuItem value="1">Sim</MenuItem>
                    <MenuItem value="0">Não</MenuItem>
                    <MenuItem value="2">Sem informação</MenuItem>
                  </Select>
                  {errors.sts?.message && (
                    <FormHelperText className={classes.helperText}>
                      {errors.sts?.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>
        )}

        <Grid item sm={6}>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <FormControl variant="outlined" className={classes.selectInput}>
                <InputLabel id="statusLabel">Status*</InputLabel>
                <Select
                  labelId="statusLabel"
                  id="textfield-status"
                  label="Status"
                  autoWidth
                  required
                  error={(!!errors?.status || !_.isEmpty(messageError?.status)) && true}
                  {...field}
                >
                  {Status.map((Option) => (
                    <MenuItem key={Option.statusValue} value={Option.statusValue}>
                      {Option.statusName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.status?.message && (
                  <FormHelperText className={classes.helperText}>
                    {errors.status?.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>
      </Grid>
      <ModalPostTechnologyGenetic label={title.label} nameField={title.nameField} />
    </>
  )
}
export default GrowCropsForms
